import { v4 as uuid4 } from 'uuid';

import { $chatCallbacks, $chatThreadState } from './chat-state';
import {
  ACTIVE_NEW_CHAT_DRAFT_KEY,
  getChatDraftKey,
  writeChatDraft,
} from '../ChatPanel/chat-draft-storage';

/**
 * Resets the chat thread state to the state of a new chat.
 * This prevents state accumulation and memory leaks when switching between threads.
 */
export function $resetChatThreadsState() {
  $chatThreadState.autoScrollAIChatItem = null;
  $chatThreadState.suggestionVisibilityByGroupId = {};
  $chatThreadState.thinkingMessage = null;
}

/**
 * Puts a prompt into the chat input for the user to send, from anywhere in
 * the app.
 *
 * The chat pane may not be the one showing when this is called: the editor's
 * playbook drawer asks for a prompt while its own pane is visible and the
 * chat's is hidden, and a hidden pane has relinquished its callbacks. The
 * draft is then queued, and the panel drains the queue when it registers
 * again. Only one draft is held, the latest, because a second request means
 * the first is no longer what the user wants.
 */
export function requestPromptDraft(markdown: string): void {
  const wasSet = $chatCallbacks.setPromptDraft?.(markdown) ?? false;
  $chatCallbacks.pendingPromptDraft = wasSet ? null : markdown;
}

/**
 * Holds a draft until the chat panel changes scope and registers the input for
 * that scope. Use this when leaving an existing thread for a new chat: writing
 * through the current callback would put the draft in the old thread's input.
 */
export function queuePromptDraft(markdown: string): void {
  $chatCallbacks.pendingPromptDraft = markdown;
}

/** Start a separate unsent conversation without replacing another conversation's draft. */
export function startNewChatDraft(markdown: string) {
  const id = uuid4();
  writeChatDraft(getChatDraftKey(null, id), markdown);
  writeChatDraft(ACTIVE_NEW_CHAT_DRAFT_KEY, id);
  $chatCallbacks.pendingPromptDraft = null;
  $chatThreadState.newChatDraftId = id;
}
